
import React, { useState, useEffect, useRef } from 'react';
import { Seat, SeatType, Attendee, ConferenceConfig, SearchResult, LogicalCoords } from '../types';
import { generateGrid, getPriorityOrder, processAttendees, calculateLogicalPositions, generateCSV, generateSeatOrderList, parseAttendeeLabel, getAttendeeIdentityKey, attendeeMatchesQuery, attendeeIncludesQuery, getAttendeeFullLabel } from '../services/coreLogic';
import * as SecurityService from '../services/security';
import { API } from '../services/api';
import { SeatMap } from './SeatMap';
import { Spotlight } from './Spotlight';
import { LogConsole } from './LogConsole';

const DEFAULT_ROWS = 32;
const DEFAULT_COLS = 9;

interface AdminPanelProps {
  onLogout: () => void;
  autoAuth?: boolean;
}

interface DuplicateItem {
    name: string;
    originalIndex: number;
}

export const AdminPanel: React.FC<AdminPanelProps> = ({ onLogout, autoAuth = false }) => {
  // --- Security State ---
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginPwd, setLoginPwd] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [hasTotpSetup, setHasTotpSetup] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [dbType, setDbType] = useState<string>('');
  
  // Optimistic Locking
  const [dataVersion, setDataVersion] = useState<number>(0);
  
  // Security Modal Internal State
  const [secStep, setSecStep] = useState<'menu' | 'setup_totp' | 'change_pwd' | 'unlock' | 'confirm_lock' | 'verify_old_totp'>('menu');
  const [newTotpSecret, setNewTotpSecret] = useState('');
  const [totpInput, setTotpInput] = useState('');
  const [newAdminPwd, setNewAdminPwd] = useState('');
  // 中文注释：verify_totp 成功后拿到的一次性凭据，用于换发新的超级管理员密钥。
  const [totpTicket, setTotpTicket] = useState('');

  // --- Regular State ---
  const [activeTab, setActiveTab] = useState<'layout' | 'attendees' | 'view'>('attendees');
  const [config, setConfig] = useState<ConferenceConfig>({ 
      rows: DEFAULT_ROWS, 
      cols: DEFAULT_COLS, 
      title: '2024年度会议',
      useSpacing: false,
      spacingRowCount: 0,
  });
  const [seats, setSeats] = useState<Seat[]>([]);
  
  // History State
  const [history, setHistory] = useState<Seat[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Editor State
  const [paintTool, setPaintTool] = useState<string>(SeatType.STANDARD);
  const [isInputMode, setIsInputMode] = useState<boolean>(false);
  const [editingSeat, setEditingSeat] = useState<Seat | null>(null);
  const [manualInputValue, setManualInputValue] = useState('');
  
  // Attendee Management State
  const [rawAttendees, setRawAttendees] = useState('');
  const [rawLeaves, setRawLeaves] = useState('');
  const [duplicateResolutionQueue, setDuplicateResolutionQueue] = useState<DuplicateItem[]>([]);
  const [currentDuplicate, setCurrentDuplicate] = useState<DuplicateItem | null>(null);
  const [duplicateRemark, setDuplicateRemark] = useState(''); 
  const [lastSuccessMsg, setLastSuccessMsg] = useState('');
  const [resolvedAttendees, setResolvedAttendees] = useState<Attendee[]>([]);
  
  // System Logs
  const [logs, setLogs] = useState<string[]>([]);
  
  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [activeSpotlight, setActiveSpotlight] = useState<string | null>(null);

  // UI Modals
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printOrientation, setPrintOrientation] = useState<'portrait' | 'landscape'>('portrait');

  const [logicalMap, setLogicalMap] = useState<Map<string, LogicalCoords>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Initialization & Data Fetching ---
  const loadServerData = async () => {
      try {
          setIsLoading(true);
          const state = await API.getState();
          
          setIsLocked(state.isLocked);
          setHasTotpSetup(state.hasTotp);
          setDbType(state.dbType || 'Unknown');
          setDataVersion(state.version);
          
          if (state.seats && state.seats.length > 0) {
              setSeats(state.seats);
              setConfig(state.config);
              setHistory([state.seats]);
              setHistoryIndex(0);
          } else {
              initGrid();
          }
      } catch (e) {
          console.error(e);
          addLog("⚠️ 无法连接服务器或数据库为空");
          initGrid();
      } finally {
          setIsLoading(false);
      }
  };

  useEffect(() => {
    // 1. Authenticate
    if (autoAuth) {
        setIsAuthenticated(true);
        loadServerData();
    }
  }, [autoAuth]);

  useEffect(() => {
    if (seats.length > 0) {
        setLogicalMap(calculateLogicalPositions(seats, config.rows, config.cols));
    }
  }, [seats, config.rows, config.cols]);

  const addLog = (msg: string) => {
      const time = new Date().toLocaleTimeString('zh-CN', { hour12: false });
      setLogs(prev => [`[${time}] ${msg}`, ...prev]);
  };

  const handleLogin = async (e: React.FormEvent) => {
      e.preventDefault();
      try {
          setIsLoading(true);
          const res = await API.login(loginPwd);
          SecurityService.setSessionToken(res.token);
          setIsAuthenticated(true);
          await loadServerData();
      } catch (err: any) {
          if (err.message.includes("RATE_LIMIT")) {
              alert("❌ 登录尝试过于频繁，请1分钟后再试。");
          } else {
              alert("登录失败: " + err.message.replace("AUTH: ", ""));
          }
      } finally {
          setIsLoading(false);
      }
  };

  // --- Security Functions ---

  const openSecurityModal = async () => {
      // Re-fetch state to be sure
      try {
          const state = await API.getState();
          setIsLocked(state.isLocked);
          setHasTotpSetup(state.hasTotp);
          setDbType(state.dbType || 'Unknown');
          setDataVersion(state.version);
      } catch(e) {}
      
      setSecStep('menu');
      setTotpInput('');
      setShowSecurityModal(true);
  };

  const initTotpSetup = async () => {
      if (hasTotpSetup) {
          setTotpInput('');
          setTotpTicket('');
          setSecStep('verify_old_totp');
      } else {
          startTotpSetup();
      }
  };

  const verifyOldTotpAndProceed = async () => {
      if (!/^\d{6}$/.test(totpInput)) { alert("请输入 6 位数字动态码。"); return; }
      try {
          // 中文注释：服务端校验旧动态码并签发 5 分钟一次性 ticket；
          // 因为动态码只有 30 秒有效期，扫码后输入新码时旧码早已过期，必须靠 ticket 传递“已验证”状态。
          const res = await API.verifyTotp(SecurityService.getSessionToken(), totpInput);
          setTotpTicket(res.ticket || '');
          startTotpSetup();
      } catch (e: any) {
          alert("验证失败: " + e.message);
      }
  };

  const startTotpSetup = async () => {
      try {
          const { secret } = await API.setupTotpInit(SecurityService.getSessionToken());
          setNewTotpSecret(secret); 
          setSecStep('setup_totp');
          setTotpInput('');
      } catch (e: any) {
          alert("初始化失败: " + e.message);
      }
  };

  const verifyAndSaveTotp = async () => {
      if (!/^\d{6}$/.test(totpInput)) { alert("请输入 6 位数字动态码。"); return; }
      try {
          await API.setupTotpVerify(SecurityService.getSessionToken(), totpInput, newTotpSecret, totpTicket);
          alert("✅ 验证成功！超级管理员动态密码已启用。");
          addLog("🔐 超级管理员(Google Authenticator) 设置成功");
          setHasTotpSetup(true);
          setTotpTicket('');
          setSecStep('menu');
      } catch (e: any) {
          alert("❌ 验证失败: " + e.message);
      }
  };

  const handleToggleLock = (e?: React.MouseEvent) => {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      
      if (isLocked) {
          setSecStep('unlock');
          setTotpInput('');
      } else {
          if (!hasTotpSetup) {
              alert("请先设置超级管理员(Google Authenticator)才能使用锁定功能。");
              return;
          }
          setSecStep('confirm_lock');
      }
  };

  const executeLock = async () => {
       try {
           await API.lockSystem(SecurityService.getSessionToken()); 
           setIsLocked(true);
           addLog("🔒 布局已锁定 (48小时自动失效)");
           setSecStep('menu');
       } catch (e: any) {
           alert("锁定失败: " + e.message);
       }
  };

  const handleUnlockVerify = async () => {
      try {
          await API.unlockSystem(SecurityService.getSessionToken(), totpInput);
          setIsLocked(false);
          addLog("🔓 布局已解锁");
          if (secStep === 'unlock') setSecStep('menu');
          else setShowSecurityModal(false);
      } catch (e: any) {
          alert("解锁失败: " + e.message);
      }
  };

  const handleSetAdminPwd = async () => {
      if (isLocked) { alert("锁定状态下无法修改密码"); return; }
      // 中文注释：与后端保持一致——后台密码必须以 admin 开头，用于和游客姓名查询区分。
      if (!newAdminPwd.toLowerCase().startsWith('admin')) {
          alert("后台密码必须以 admin 开头（例如 admin996），这样系统才能区分“查询姓名”和“进入后台”。");
          return;
      }
      if (newAdminPwd.length < 6 || newAdminPwd.length > 50) {
          alert("密码长度需在 6-50 位之间。");
          return;
      }
      try {
          await API.changePassword(SecurityService.getSessionToken(), newAdminPwd);
          alert("普通后台密码已更新。\n\n注意：超过 48 小时未再修改，密码会自动恢复为默认值 admin996。");
          setNewAdminPwd('');
          setSecStep('menu');
      } catch (e: any) {
          alert("修改失败: " + e.message);
      }
  };

  // --- Standard Functions ---

  const persistToStorage = async (currentSeats: Seat[], currentConfig: ConferenceConfig) => {
      if (isLocked) return;
      try {
          const res = await API.saveLayout(currentSeats, currentConfig, SecurityService.getSessionToken(), dataVersion);
          setDataVersion(res.newVersion); // Update local version on success
          addLog("☁️ 自动保存成功");
      } catch (e: any) {
          console.error(e);
          const msg = e.message || '';
          
          if (msg.includes("CONFLICT")) {
              alert("⚠️ 数据冲突：后台数据已被其他人修改！\n\n请刷新页面获取最新布局，然后重新尝试。");
              addLog("❌ 保存失败: 版本冲突");
          } else if (msg.includes("AUTH")) {
              alert("登录凭证已过期，请重新登录");
              onLogout();
          } else {
             addLog("❌ 保存失败: " + msg);
          }
          
          if (msg.includes("Locked")) {
              setIsLocked(true); // Sync state
          }
          throw e;
      }
  };

  const updateSeats = (newSeats: Seat[], recordHistory = true) => {
      if (isLocked && history.length > 0) {
          addLog("⛔ 操作无效: 布局锁定中");
          return;
      }
      setSeats(newSeats);
      if (recordHistory) {
          const newHistory = history.slice(0, historyIndex + 1);
          newHistory.push(newSeats);
          if (newHistory.length > 30) newHistory.shift();
          setHistory(newHistory);
          setHistoryIndex(newHistory.length - 1);
      }
  };

  const undo = () => {
      if (historyIndex > 0 && !isLocked) {
          const newIndex = historyIndex - 1;
          setHistoryIndex(newIndex);
          setSeats(history[newIndex]);
      }
  };

  const redo = () => {
      if (historyIndex < history.length - 1 && !isLocked) {
          const newIndex = historyIndex + 1;
          setHistoryIndex(newIndex);
          setSeats(history[newIndex]);
      }
  };

  const initGrid = () => {
    if (isLocked && seats.length > 0) return; 
    const newSeats = generateGrid(config.rows, config.cols);
    setSeats(newSeats);
    setHistory([newSeats]);
    setHistoryIndex(0);
    addLog("网格初始化");
  };

  const saveLayoutTemplate = async () => {
    if (isLocked) { alert("布局锁定中"); return; }
    try {
        await persistToStorage(seats, config);
        alert('布局已提交到服务器！');
    } catch(e) {
        // Handled in persistToStorage
    }
  };

  // --- Import / Export Functions ---
  const handleExportJSON = () => {
      const data = JSON.stringify({ config, seats }, null, 2);
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `smartseat-layout-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      addLog("📤 导出 JSON 成功");
  };

  const handleExportCSV = () => {
      const csv = generateCSV(seats, config.rows, config.cols);
      const blob = new Blob(["\ufeff"+csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `smartseat-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      addLog("📤 导出 CSV 成功");
  };

  const handleExportSeatOrderTxt = () => {
      const txtContent = generateSeatOrderList(seats, config.rows, config.cols);
      const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `座次表顺序名单-${new Date().toISOString().slice(0, 10)}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      addLog("📤 导出顺序名单成功");
  };

  const handleImportTrigger = () => {
      if(isLocked) { alert("锁定状态无法导入"); return; }
      fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
          try {
              const json = JSON.parse(event.target?.result as string);
              if (json.seats && json.config) {
                  // Validate basic structure
                  if(Array.isArray(json.seats) && typeof json.config.rows === 'number') {
                       setConfig(json.config);
                       updateSeats(json.seats);
                       addLog("📥 导入布局成功");
                  } else {
                      alert("文件格式不正确");
                  }
              } else {
                  alert("无效的布局文件");
              }
          } catch (err) {
              alert("解析 JSON 失败");
          }
      };
      reader.readAsText(file);
      // Reset input
      e.target.value = '';
  };

  const handleReturnToSearch = async () => {
      // 中文注释：登出时同步删除服务端会话，避免 token 在 2 小时内仍可被复用。
      await API.logout(SecurityService.getSessionToken());
      SecurityService.setSessionToken('');
      onLogout();
  };

  const handleResetLayout = () => {
    if (isLocked) return;
    setShowResetConfirmModal(true);
  };

  const confirmResetSystem = async () => {
      const newSeats = generateGrid(config.rows, config.cols);
      setSeats(newSeats);
      setHistory([newSeats]);
      setHistoryIndex(0);
      setRawAttendees('');
      setRawLeaves('');
      setResolvedAttendees([]);
      setDuplicateResolutionQueue([]);
      setCurrentDuplicate(null);
      setLogs([]);
      
      try {
        await persistToStorage(newSeats, config);
      } catch(e) { console.error("Auto save reset failed", e); }
      
      const time = new Date().toLocaleTimeString('zh-CN', { hour12: false });
      setLogs([`[${time}] ⚠️ 系统布局已重置`]);
      setShowResetConfirmModal(false);
  };

  const handleConfigChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLocked) return;
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
        const newConfig = { ...config, [name]: checked };
        setConfig(newConfig);
        persistToStorage(seats, newConfig).catch(() => {});
        addLog(checked ? "✨ 开启间隔就坐模式" : "✨ 关闭间隔就坐模式");
        return;
    }

    if (name === 'title') {
      setConfig(prev => ({ ...prev, title: value }));
      return;
    }

    const num = parseInt(value);
    if (!isNaN(num) && num >= 0) {
      setConfig(prev => ({ ...prev, [name]: num }));
    }
  };

  // 间隔就坐开关关闭时，允许配置“前几排仍保持间隔，从下一排起不间隔”。
  const handleSpacingRowCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLocked) return;
    const raw = e.target.value;
    let value = parseInt(raw, 10);
    if (raw === '' || isNaN(value) || value < 0) value = 0;
    const newConfig = { ...config, spacingRowCount: value };
    setConfig(newConfig);
    persistToStorage(seats, newConfig).catch(() => {});
  };

  const applyGridSize = () => {
    if (isLocked) return;
    if (window.confirm("调整大小将重置所有座位。是否继续？")) {
      initGrid();
    }
  };

  const handleSeatClick = (seat: Seat) => {
    if (activeTab !== 'layout' || isLocked) return;

    if (isInputMode) {
        setEditingSeat(seat);
        if (seat.type === SeatType.AISLE) {
            setManualInputValue(seat.label || '');
        } else {
            setManualInputValue(seat.attendeeName || '');
        }
        return;
    }

    const newSeats = [...seats];
    const index = newSeats.findIndex(s => s.id === seat.id);
    
    if (paintTool === 'aisle-single') {
        newSeats[index] = { ...newSeats[index], type: SeatType.AISLE, isManual: true };
    } else if (paintTool === 'aisle-row') {
        newSeats.forEach((s, i) => {
            if (s.row === seat.row) newSeats[i] = { ...newSeats[i], type: SeatType.AISLE, isManual: true };
        });
    } else if (paintTool === 'aisle-col') {
        newSeats.forEach((s, i) => {
            if (s.col === seat.col && s.row >= seat.row) newSeats[i] = { ...newSeats[i], type: SeatType.AISLE, isManual: true };
        });
    } else {
        newSeats[index] = { ...newSeats[index], type: paintTool as SeatType, isManual: paintTool !== SeatType.STANDARD };
    }
    updateSeats(newSeats);
  };

  const handleManualInputConfirm = () => {
      if (!editingSeat || isLocked) return;
      const newSeats = [...seats];
      const index = newSeats.findIndex(s => s.id === editingSeat.id);
      const text = manualInputValue;
      if (editingSeat.type === SeatType.AISLE) {
          newSeats[index] = { ...newSeats[index], label: text };
      } else {
          const hasText = text.trim().length > 0;
          // 修复：手动指定人员时同步识别括号备注，保证后续重名查询和自动排座排除逻辑一致。
          const parsedManual = hasText ? parseAttendeeLabel(text) : { name: '', remark: undefined };
          newSeats[index] = {
              ...newSeats[index],
              attendeeName: hasText ? parsedManual.name : undefined,
              attendeeRemark: hasText ? parsedManual.remark : undefined,
              isManual: hasText
          };
      }
      updateSeats(newSeats);
      setEditingSeat(null);
      setManualInputValue('');
  };

  const handleProcessAttendees = () => {
    if (isLocked) return;
    const { valid, duplicates } = processAttendees(rawAttendees, rawLeaves);
    setDuplicateResolutionQueue(duplicates);
    setResolvedAttendees(valid);
    addLog(`名单处理: 有效 ${valid.length} 人, 重名待处理 ${duplicates.length} 组`);
    if (duplicates.length > 0) {
      setCurrentDuplicate(duplicates[0]);
      setDuplicateRemark('');
      setLastSuccessMsg('');
    } else {
      alert(`名单处理完成：${valid.length} 人有效。`);
    }
  };

  // Fix for: Cannot find name 'handleResolveDuplicate'.
  // This function processes a duplicate name by adding a remark and moving to the next item in the resolution queue.
  const handleResolveDuplicate = (remark: string) => {
    if (!currentDuplicate) return;

    const newAttendee: Attendee = {
        id: crypto.randomUUID(),
        name: currentDuplicate.name,
        remark: remark,
        status: 'pending',
        originalIndex: currentDuplicate.originalIndex
    };

    // 修复：重名处理后按原始名单位置回填，避免处理过的重名人员被追加到座次最后。
    setResolvedAttendees(prev => [...prev, newAttendee].sort((a, b) => (a.originalIndex ?? 0) - (b.originalIndex ?? 0)));
    setLastSuccessMsg(`已添加 ${currentDuplicate.name} (${remark || '无备注'})`);
    
    const nextQueue = duplicateResolutionQueue.slice(1);
    setDuplicateResolutionQueue(nextQueue);
    
    if (nextQueue.length > 0) {
        setCurrentDuplicate(nextQueue[0]);
        setDuplicateRemark('');
    } else {
        setCurrentDuplicate(null);
        setDuplicateRemark('');
        alert("所有重名人员已处理完毕。");
    }
  };

  const handleAutoAssign = () => {
      if (isLocked) return;
      if (resolvedAttendees.length === 0) { alert('请先录入名单'); return; }

      const manualIdentityKeys = new Set<string>();
      seats.forEach(s => { 
          if (s.isManual && s.attendeeName && s.type === SeatType.STANDARD) {
              // 修复：按“姓名+备注”识别手动指定人员，防止重名人员被错误地整组过滤。
              manualIdentityKeys.add(getAttendeeIdentityKey(s.attendeeName, s.attendeeRemark));
          }
      });
      
      // 修复：自动排座前按原始名单顺序排序，重名处理不会改变人员先后。
      const attendeesToAssign = [...resolvedAttendees]
          .sort((a, b) => (a.originalIndex ?? 0) - (b.originalIndex ?? 0))
          .filter(a => !manualIdentityKeys.has(getAttendeeIdentityKey(a.name, a.remark)));

      if (attendeesToAssign.length === 0) { alert("所有名单人员都已在座位上。"); return; }
      
      // Pass the useSpacing flag to the sorting logic
      const prioritySeats = getPriorityOrder(seats, config.rows, config.cols, config.useSpacing, config.spacingRowCount);
      
      if (prioritySeats.length === 0) { alert("没有可用的普通座位！"); return; }

      const newSeats = [...seats];
      const seatIdToIndex = new Map(newSeats.map((s, i) => [s.id, i]));

      // Clear existing non-manual assignments first to avoid leftover spacing conflicts
      seats.forEach(s => {
          if (!s.isManual && s.type === SeatType.STANDARD) {
               const idx = seatIdToIndex.get(s.id);
               if (idx !== undefined) newSeats[idx] = { ...newSeats[idx], attendeeName: undefined, attendeeRemark: undefined };
          }
      });

      let assignedCount = 0;
      for (const attendee of attendeesToAssign) {
          if (assignedCount >= prioritySeats.length) break;
          const seatId = prioritySeats[assignedCount].id;
          const index = seatIdToIndex.get(seatId);
          if (index !== undefined) {
              newSeats[index] = { ...newSeats[index], attendeeName: attendee.name, attendeeRemark: attendee.remark, isManual: false };
              assignedCount++;
          }
      }
      updateSeats(newSeats);
      // 会议标题随本次自动排座结果一并保存到数据库，无需再到布局页单独保存。
      persistToStorage(newSeats, config).catch(e => {
        // Just log
      });
      alert(`分配完成！成功安排 ${assignedCount} 人。${config.useSpacing ? '(间隔模式已开启)' : ''}`);
  };

  const handleClearAuto = () => {
      if (isLocked) return;
      const newSeats = seats.map(s => !s.isManual ? { ...s, attendeeName: undefined, attendeeRemark: undefined, isManual: false } : s);
      updateSeats(newSeats);
      addLog("🧹 已清空自动分配");
  };

  const handleSearch = () => {
      if (!searchQuery.trim()) return;
      const results: SearchResult[] = [];
      const query = searchQuery.trim();
      seats.forEach(s => {
          if (s.attendeeName) {
              // 修复：后台查询同样支持“王辉”匹配“王辉（大）/王辉（小）”。
              if (attendeeMatchesQuery(s.attendeeName, s.attendeeRemark, query)) results.push({ seat: s, matchType: 'exact' });
              else if (attendeeIncludesQuery(s.attendeeName, s.attendeeRemark, query)) results.push({ seat: s, matchType: 'fuzzy' });
          }
      });
      setSearchResults(results);
      if (results.length === 0) alert("未找到该人员");
  };

  // --- Print Handling ---
  const handleOpenPrintModal = () => {
      setPrintOrientation('portrait'); // Default
      setShowPrintModal(true);
  };

  const handleExecutePrint = () => {
      // 1. Inject Styles for @page
      const existingStyle = document.getElementById('print-page-style');
      if (existingStyle) existingStyle.remove();

      const style = document.createElement('style');
      style.id = 'print-page-style';
      // Dynamically set @page size
      style.innerHTML = `@media print { @page { size: ${printOrientation}; } }`;
      document.head.appendChild(style);

      // 2. Set Class on Body for Zoom Control
      document.body.classList.remove('print-mode-portrait', 'print-mode-landscape');
      document.body.classList.add(`print-mode-${printOrientation}`);

      // 3. Close Modal & Print
      setShowPrintModal(false);
      setTimeout(() => {
          window.print();
      }, 100);
  };


  // --- Render Login Screen ---
  if (!isAuthenticated) {
      return (
          <div className="h-screen bg-slate-100 flex items-center justify-center flex-col">
              <div className="bg-white p-8 rounded-lg shadow-xl w-96">
                  <h1 className="text-xl font-bold mb-4 text-center">后台管理登录</h1>
                  <form onSubmit={handleLogin}>
                      <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">管理员密码</label>
                          <input 
                            type="password" 
                            className="w-full border rounded p-2 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary" 
                            value={loginPwd}
                            onChange={e => setLoginPwd(e.target.value)}
                            autoFocus
                            placeholder="默认为 admin"
                          />
                      </div>
                      <button type="submit" disabled={isLoading} className="w-full bg-primary text-white py-2 rounded hover:bg-blue-600 font-bold shadow flex justify-center">
                          {isLoading ? <i className="fa-solid fa-spinner fa-spin"></i> : "进入"}
                      </button>
                  </form>
                  <button onClick={onLogout} className="w-full mt-4 text-sm text-gray-500 hover:text-gray-800 underline">返回查询页</button>
              </div>
          </div>
      );
  }

  return (
    <div className="flex h-screen bg-slate-100 font-sans relative">
      {/* Sidebar */}
      <div className="w-80 bg-white shadow-xl flex flex-col z-20 border-r border-gray-200 no-print">
        <div className="p-4 border-b border-gray-100 bg-slate-800 text-white">
            <h1 className="text-xl font-bold flex items-center justify-between">
                <span className="flex items-center gap-2"><i className="fa-solid fa-cog"></i> 后台管理</span>
                <div className="flex gap-2">
                    <button onClick={openSecurityModal} className={`text-xs px-2 py-1 rounded transition-colors ${isLocked ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' : 'bg-slate-600 hover:bg-slate-500 text-gray-200'}`} title="安全中心">
                        <i className={`fa-solid ${isLocked ? 'fa-lock' : 'fa-shield-halved'}`}></i> {isLocked && '已锁定'}
                    </button>
                    <button onClick={handleReturnToSearch} className="text-xs bg-slate-600 px-2 py-1 rounded hover:bg-slate-500" title="退出">
                        <i className="fa-solid fa-arrow-right-from-bracket"></i>
                    </button>
                </div>
            </h1>
            <p className="text-xs text-gray-400 mt-1 flex justify-between">
                <span>Vercel Secure Mode</span>
                {isLocked && <span className="text-red-400 font-bold"><i className="fa-solid fa-lock"></i> 禁止编辑 (48h)</span>}
            </p>
        </div>

        <div className="flex border-b border-gray-100">
            <button onClick={() => setActiveTab('layout')} className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'layout' ? 'text-primary border-b-2 border-primary bg-blue-50' : 'text-gray-500 hover:text-gray-700'}`}>布局</button>
            <button onClick={() => setActiveTab('attendees')} className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'attendees' ? 'text-primary border-b-2 border-primary bg-blue-50' : 'text-gray-500 hover:text-gray-700'}`}>人员</button>
            <button onClick={() => setActiveTab('view')} className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'view' ? 'text-primary border-b-2 border-primary bg-blue-50' : 'text-gray-500 hover:text-gray-700'}`}>预览</button>
        </div>

        {/* --- SIDEBAR CONTENT --- */}
        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar flex flex-col relative">
            {isLocked && activeTab !== 'view' && (
                <div className="absolute inset-0 z-50 bg-white/60 backdrop-blur-[2px] flex flex-col items-center justify-center text-center p-4">
                    <div className="bg-white p-6 rounded-lg shadow-xl border border-red-100">
                        <i className="fa-solid fa-lock text-4xl text-red-400 mb-3"></i>
                        <h3 className="font-bold text-gray-800 text-lg">布局已锁定</h3>
                        <p className="text-xs text-gray-500 mb-4 mt-1">服务器已拒绝所有修改请求。<br/>48小时后自动解锁。</p>
                        <button onClick={() => { setSecStep('unlock'); setShowSecurityModal(true); }} className="bg-primary text-white px-4 py-2 rounded text-sm shadow hover:bg-blue-600 w-full">
                            使用动态密码解锁
                        </button>
                    </div>
                </div>
            )}

            {activeTab === 'layout' && (
                <div className={`space-y-6 flex-1 flex flex-col ${isLocked ? 'filter blur-[1px] pointer-events-none' : ''}`}>
                    <div className="space-y-3">
                        <label className="text-sm font-bold text-gray-700">基础设置</label>
                        <div className="flex gap-2 items-center">
                             <input type="number" name="rows" value={config.rows} onChange={handleConfigChange} disabled={isLocked} className="w-1/2 px-2 py-1 border rounded text-sm bg-white text-gray-900"/>
                             <input type="number" name="cols" value={config.cols} onChange={handleConfigChange} disabled={isLocked} className="w-1/2 px-2 py-1 border rounded text-sm bg-white text-gray-900"/>
                        </div>
                        <button onClick={applyGridSize} disabled={isLocked} className="w-full py-1 bg-gray-100 text-gray-600 text-xs rounded hover:bg-gray-200">应用尺寸调整</button>
                    </div>

                    <div className="pt-3 border-t border-gray-200 mt-2">
                         <div className="flex items-center justify-between bg-sky-50 p-3 rounded border border-sky-100">
                            <div className="flex flex-col">
                                <span className="text-sm font-bold text-sky-900">间隔就坐模式</span>
                                <span className="text-[10px] text-sky-600">自动排座时每人间隔一个空位</span>
                            </div>
                            <button onClick={() => handleConfigChange({ target: { name: 'useSpacing', checked: !config.useSpacing, type: 'checkbox' } } as any)} disabled={isLocked} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${config.useSpacing ? 'bg-sky-600' : 'bg-gray-300'}`}>
                                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${config.useSpacing ? 'translate-x-5' : 'translate-x-0'}`} />
                            </button>
                        </div>
                        {!config.useSpacing && (
                            <div className="mt-2 bg-gray-50 p-3 rounded border border-gray-100">
                                <label className="text-xs font-bold text-gray-600 mb-1 block">从第几排开始不间隔就坐</label>
                                <input
                                    type="number"
                                    name="spacingRowCount"
                                    min={0}
                                    step={1}
                                    value={config.spacingRowCount ?? 0}
                                    onChange={handleSpacingRowCountChange}
                                    disabled={isLocked}
                                    className="w-full px-2 py-1 border rounded text-sm bg-white text-gray-900"
                                    placeholder="默认 0"
                                />
                                <p className="text-[10px] text-gray-400 mt-1">输入 5 表示前 5 排仍间隔就坐，从第 6 排起不间隔；0 = 全部不间隔。</p>
                            </div>
                        )}
                    </div>

                     <div className="space-y-3 pt-4">
                         <div className="flex justify-between items-center">
                             <label className="text-sm font-bold text-gray-700">绘制工具</label>
                             <div className="flex gap-1">
                                 <button onClick={undo} disabled={historyIndex <= 0 || isLocked} className="p-1 px-2 rounded text-xs bg-gray-100 hover:bg-gray-200" title="撤销"><i className="fa-solid fa-rotate-left"></i></button>
                                 <button onClick={redo} disabled={historyIndex >= history.length - 1 || isLocked} className="p-1 px-2 rounded text-xs bg-gray-100 hover:bg-gray-200" title="重做"><i className="fa-solid fa-rotate-right"></i></button>
                             </div>
                         </div>
                         <div className={`space-y-2 ${isInputMode ? 'opacity-50 pointer-events-none' : ''}`}>
                             <div className="grid grid-cols-2 gap-2">
                                 <button onClick={() => setPaintTool(SeatType.STANDARD)} className={`p-2 rounded border text-xs flex items-center justify-center gap-2 transition-colors ${paintTool === SeatType.STANDARD ? 'bg-blue-100 border-blue-500 text-blue-900 font-bold' : 'bg-white hover:bg-gray-50'}`}>
                                     <div className="w-3 h-3 bg-sky-200 border border-sky-300 rounded-sm"></div> 普通座位
                                 </button>
                                 <button onClick={() => setPaintTool(SeatType.STAGE)} className={`p-2 rounded border text-xs flex items-center justify-center gap-2 transition-colors ${paintTool === SeatType.STAGE ? 'bg-amber-100 border-amber-500 text-amber-900 font-bold' : 'bg-white hover:bg-gray-50'}`}>
                                     <div className="w-3 h-3 bg-amber-100 border border-amber-400 rounded-sm"></div> 主席台
                                 </button>
                                 <button onClick={() => setPaintTool(SeatType.EMPTY)} className={`p-2 rounded border text-xs flex items-center justify-center gap-2 transition-colors ${paintTool === SeatType.EMPTY ? 'bg-gray-100 border-gray-500 text-gray-900 font-bold' : 'bg-white hover:bg-gray-50'}`}>
                                     <div className="w-3 h-3 border border-dashed border-gray-400 rounded-sm"></div> 空置/删除
                                 </button>
                                 <button onClick={() => setPaintTool(SeatType.UNAVAILABLE)} className={`p-2 rounded border text-xs flex items-center justify-center gap-2 transition-colors ${paintTool === SeatType.UNAVAILABLE ? 'bg-red-100 border-red-500 text-red-900 font-bold' : 'bg-white hover:bg-gray-50'}`}>
                                     <div className="w-3 h-3 bg-red-50 border border-red-200 rounded-sm"></div> 不可用
                                 </button>
                             </div>
                             
                             <div className="pt-2 border-t border-gray-100">
                                <label className="text-xs font-bold text-gray-500 mb-1.5 block">过道设置 (Aisle)</label>
                                <div className="grid grid-cols-3 gap-1">
                                    <button onClick={() => setPaintTool('aisle-single')} className={`py-2 px-1 rounded border text-xs flex flex-col items-center gap-1 transition-all ${paintTool === 'aisle-single' ? 'bg-gray-700 text-white border-gray-800 shadow-inner' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                                        <i className="fa-regular fa-square"></i> 单格
                                    </button>
                                    <button onClick={() => setPaintTool('aisle-row')} className={`py-2 px-1 rounded border text-xs flex flex-col items-center gap-1 transition-all ${paintTool === 'aisle-row' ? 'bg-gray-700 text-white border-gray-800 shadow-inner' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                                        <i className="fa-solid fa-arrows-left-right"></i> 整行
                                    </button>
                                    <button onClick={() => setPaintTool('aisle-col')} className={`py-2 px-1 rounded border text-xs flex flex-col items-center gap-1 transition-all ${paintTool === 'aisle-col' ? 'bg-gray-700 text-white border-gray-800 shadow-inner' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                                        <i className="fa-solid fa-arrow-down-long"></i> 下方列
                                    </button>
                                </div>
                             </div>
                         </div>
                    </div>

                    <div className="pt-3 border-t border-gray-200 mt-2">
                         <div className="flex items-center justify-between bg-indigo-50 p-3 rounded border border-indigo-100">
                            <span className="text-sm font-bold text-indigo-900">手动指定人员</span>
                            <button onClick={() => setIsInputMode(!isInputMode)} disabled={isLocked} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${isInputMode ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isInputMode ? 'translate-x-5' : 'translate-x-0'}`} />
                            </button>
                        </div>
                    </div>

                    <div className="pt-3 border-t border-gray-200 mt-2 space-y-2">
                        <label className="text-sm font-bold text-gray-700">数据管理</label>
                        <div className="grid grid-cols-2 gap-2">
                            <button onClick={handleImportTrigger} disabled={isLocked} className="py-2 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50 flex items-center justify-center gap-1">
                                <i className="fa-solid fa-file-import"></i> 导入JSON
                            </button>
                            <button onClick={handleExportJSON} className="py-2 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50 flex items-center justify-center gap-1">
                                <i className="fa-solid fa-file-export"></i> 导出JSON
                            </button>
                            <button onClick={handleExportCSV} className="col-span-2 py-2 bg-white border border-green-200 text-green-700 rounded text-xs hover:bg-green-50 flex items-center justify-center gap-1">
                                <i className="fa-solid fa-file-csv"></i> 导出 Excel/CSV 座次表
                            </button>
                        </div>
                        <input type="file" ref={fileInputRef} className="hidden" accept=".json" onChange={handleFileChange} />
                    </div>

                    <div className="pt-4 border-t border-gray-200 grid gap-2">
                        <button onClick={saveLayoutTemplate} disabled={isLocked} className="w-full bg-secondary text-white py-2 rounded hover:bg-slate-600 transition text-sm">
                            <i className="fa-solid fa-floppy-disk mr-2"></i> 保存到服务器
                        </button>
                        <button onClick={handleResetLayout} disabled={isLocked} className="w-full bg-red-50 text-red-600 border border-red-200 py-2 rounded text-sm hover:bg-red-100 mt-2">
                            <i className="fa-solid fa-trash-can mr-2"></i> 重置系统
                        </button>
                    </div>
                    <LogConsole logs={logs} />
                </div>
            )}

            {activeTab === 'attendees' && (
                <div className={`space-y-6 flex-1 flex flex-col ${isLocked ? 'filter blur-[1px] pointer-events-none' : ''}`}>
                     {/* 会议标题放在人员页首项，点击“自动排座”时与排座结果一起保存。 */}
                     <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-700">会议标题</label>
                        <input type="text" name="title" value={config.title} onChange={handleConfigChange} disabled={isLocked} className="w-full px-3 py-2 border rounded text-sm bg-white text-gray-900" placeholder="请输入会议标题"/>
                     </div>
                     <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-700">名单录入</label>
                        <textarea className="w-full h-32 p-2 border rounded text-sm bg-white text-gray-900" value={rawAttendees} onChange={(e) => setRawAttendees(e.target.value)} disabled={isLocked}></textarea>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-700">请假/剔除名单 <span className="text-xs font-normal text-gray-400">(不安排座位)</span></label>
                        <textarea className="w-full h-20 p-2 border rounded text-sm bg-white text-gray-900" value={rawLeaves} onChange={(e) => setRawLeaves(e.target.value)} disabled={isLocked} placeholder="输入姓名，用换行或逗号分隔"></textarea>
                    </div>
                    <button onClick={handleProcessAttendees} disabled={isLocked} className="w-full bg-blue-50 text-blue-600 border border-blue-200 py-2 rounded">1. 处理名单</button>
                    <button onClick={handleAutoAssign} disabled={resolvedAttendees.length === 0 || isLocked} className="w-full bg-primary text-white py-3 rounded shadow">2. 自动排座</button>
                    <button onClick={handleClearAuto} disabled={isLocked} className="w-full py-2 text-sm text-red-500 hover:bg-red-50 rounded">清空排座</button>
                    <LogConsole logs={logs} />
                </div>
            )}
             {activeTab === 'view' && (
                <div className="space-y-4">
                     <div className="flex justify-between items-center mb-4 no-print">
                        <h3 className="font-bold text-gray-700">预览 & 打印</h3>
                        <div className="flex flex-col gap-2">
                             <button onClick={handleOpenPrintModal} className="bg-gray-800 text-white px-4 py-2 rounded text-sm hover:bg-black transition-colors w-full">
                                <i className="fa-solid fa-image mr-2"></i> 导出图片 / 打印
                            </button>
                            <button onClick={handleExportSeatOrderTxt} className="bg-white border border-green-600 text-green-700 px-4 py-2 rounded text-sm hover:bg-green-50 transition-colors w-full flex items-center justify-center gap-2">
                                <i className="fa-solid fa-list-ol"></i> 导出 Excel 座次表顺序 (.txt)
                            </button>
                        </div>
                     </div>
                     <div className="bg-white p-3 rounded-lg border shadow-sm">
                        <label className="text-xs font-bold text-gray-500 mb-1 block">内部查询</label>
                        <div className="flex gap-2">
                            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="flex-1 border rounded px-2 py-1 text-sm bg-white text-gray-900" placeholder="输入姓名..." onKeyDown={(e) => e.key === 'Enter' && handleSearch()}/>
                            <button onClick={handleSearch} className="bg-primary text-white px-3 rounded"><i className="fa-solid fa-magnifying-glass"></i></button>
                        </div>
                     </div>
                     {searchResults.map((res, idx) => (
                        <div key={idx} onClick={() => setActiveSpotlight(res.seat.id)} className="p-3 bg-white border rounded hover:bg-blue-50 cursor-pointer flex justify-between items-center">
                            {/* 修复：查询结果显示完整身份标签，重名人员能看出“大/小”等备注。 */}
                            <div><div className="font-bold text-sm">{getAttendeeFullLabel(res.seat.attendeeName || '', res.seat.attendeeRemark)}</div></div>
                            <div className="text-xs font-mono bg-gray-100 px-1 rounded">{logicalMap.get(res.seat.id)?.isValid ? `${logicalMap.get(res.seat.id)?.rowLabel}-${logicalMap.get(res.seat.id)?.colLabel}` : '特殊'}</div>
                        </div>
                     ))}
                </div>
            )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-slate-200 overflow-auto relative flex flex-col items-center p-8 print-centering-container">
           {/* Title visible only in Print */}
           <h1 className="print-title">{config.title}</h1>

           <div className={`shadow-2xl rounded-xl border border-slate-300 bg-white max-w-full my-auto ${isLocked ? 'ring-4 ring-red-100' : ''} print-scale-target`}>
                <SeatMap seats={seats} rows={config.rows} cols={config.cols} logicalMap={logicalMap} onSeatClick={handleSeatClick} readOnly={isLocked}/>
           </div>
      </div>

      {/* Print Options Modal */}
      {showPrintModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[80] backdrop-blur-sm no-print">
              <div className="bg-white p-6 rounded-lg shadow-xl w-80">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                      <i className="fa-solid fa-print"></i> 打印设置
                  </h3>
                  
                  <div className="space-y-4 mb-6">
                      <div className="flex flex-col gap-2">
                          <label className="text-sm font-bold text-gray-700">纸张方向 (A4)</label>
                          <div className="grid grid-cols-2 gap-3">
                              <button 
                                  onClick={() => setPrintOrientation('portrait')}
                                  className={`p-3 border rounded flex flex-col items-center gap-2 transition-all ${printOrientation === 'portrait' ? 'bg-blue-50 border-blue-500 text-blue-700 ring-2 ring-blue-200' : 'hover:bg-gray-50 border-gray-200'}`}
                              >
                                  <i className="fa-regular fa-file text-2xl"></i>
                                  <span className="text-sm font-medium">纵向 (默认)</span>
                              </button>
                              <button 
                                  onClick={() => setPrintOrientation('landscape')}
                                  className={`p-3 border rounded flex flex-col items-center gap-2 transition-all ${printOrientation === 'landscape' ? 'bg-blue-50 border-blue-500 text-blue-700 ring-2 ring-blue-200' : 'hover:bg-gray-50 border-gray-200'}`}
                              >
                                  <i className="fa-regular fa-file text-2xl rotate-90"></i>
                                  <span className="text-sm font-medium">横向</span>
                              </button>
                          </div>
                      </div>
                  </div>

                  <div className="flex gap-2">
                      <button onClick={() => setShowPrintModal(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm font-medium">取消</button>
                      <button onClick={handleExecutePrint} className="flex-1 py-2 bg-gray-800 text-white rounded hover:bg-black text-sm font-bold shadow">
                          开始打印
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* Manual Input Modal */}
      {editingSeat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 no-print">
            <div className="bg-white p-6 rounded-lg shadow-xl w-80">
                <h3 className="text-lg font-bold mb-4">{editingSeat.type === SeatType.AISLE ? '编辑过道文字' : '手动指定人员'}</h3>
                <input 
                    type="text" 
                    className="w-full border p-2 rounded mb-4 bg-white text-gray-900" 
                    autoFocus 
                    value={manualInputValue} 
                    onChange={e => setManualInputValue(e.target.value)}
                    placeholder={editingSeat.type === SeatType.AISLE ? "输入过道标签 (如: A区)" : "输入人员姓名"}
                    onKeyDown={e => e.key === 'Enter' && handleManualInputConfirm()}
                />
                <div className="flex justify-end gap-2">
                    <button onClick={() => setEditingSeat(null)} className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded">取消</button>
                    <button onClick={handleManualInputConfirm} className="px-4 py-2 bg-primary text-white rounded">确认</button>
                </div>
            </div>
        </div>
      )}

      {/* Duplicate Resolution Modal */}
      {currentDuplicate && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm no-print">
              <div className="bg-white p-6 rounded-lg shadow-2xl w-96 border-l-4 border-yellow-400">
                  <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                      <i className="fa-solid fa-user-group text-yellow-500"></i> 重名处理
                  </h3>
                  <div className="mb-4 text-gray-600 text-sm">
                      检测到多个 <strong>{currentDuplicate.name}</strong>。<br/>
                      请为当前这位添加备注以示区分。
                  </div>
                  <div className="mb-2 bg-gray-50 p-2 rounded text-xs text-gray-500">
                      原始名单位置: 第 {currentDuplicate.originalIndex + 1} 位
                  </div>
                  {lastSuccessMsg && <div className="mb-2 text-green-600 text-xs font-bold animate-pulse">{lastSuccessMsg} ✅</div>}
                  
                  <input 
                      type="text" 
                      className="w-full border-2 border-yellow-100 focus:border-yellow-400 p-2 rounded mb-4 bg-white text-gray-900" 
                      autoFocus 
                      value={duplicateRemark} 
                      onChange={e => setDuplicateRemark(e.target.value)}
                      placeholder="输入备注 (例如: 财务部)"
                      onKeyDown={e => e.key === 'Enter' && handleResolveDuplicate(duplicateRemark)}
                  />
                  <div className="flex justify-end gap-2">
                      <button onClick={() => handleResolveDuplicate(duplicateRemark)} className="w-full px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-bold rounded shadow">
                          确认并处理下一个
                      </button>
                  </div>
                  <div className="mt-2 text-center text-xs text-gray-400">
                      剩余待处理: {duplicateResolutionQueue.length}
                  </div>
              </div>
          </div>
      )}

      {/* Confirm Reset Modal */}
      {showResetConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 no-print">
             <div className="bg-white p-6 rounded-lg shadow-xl w-80 border-t-4 border-red-500">
                  <h3 className="text-lg font-bold text-red-600 mb-2">⚠️ 危险操作</h3>
                  <p className="text-sm text-gray-600 mb-6">确定要重置整个系统吗？<br/>这将清空所有座位、名单和设置。</p>
                  <div className="flex gap-2">
                      <button onClick={() => setShowResetConfirmModal(false)} className="flex-1 py-2 bg-gray-100 text-gray-700 rounded">取消</button>
                      <button onClick={confirmResetSystem} className="flex-1 py-2 bg-red-600 text-white rounded font-bold">确认重置</button>
                  </div>
             </div>
        </div>
      )}

      {/* Security Modal */}
      {showSecurityModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] backdrop-blur-sm no-print">
              <div className="bg-white rounded-lg shadow-xl w-96 overflow-hidden">
                  <div className="bg-gray-800 text-white p-4 flex justify-between items-center">
                      <h3 className="font-bold"><i className="fa-solid fa-shield-halved mr-2"></i> 安全中心</h3>
                      <button onClick={() => setShowSecurityModal(false)} className="text-gray-400 hover:text-white"><i className="fa-solid fa-xmark"></i></button>
                  </div>
                  
                  {secStep === 'menu' && (
                      <div className="p-6 space-y-4">
                           <div className="flex items-center justify-between bg-blue-50 px-4 py-2 rounded border border-blue-100 mb-2">
                               <span className="text-sm font-bold text-blue-800">
                                   <i className="fa-solid fa-database mr-2"></i>存储引擎
                               </span>
                               <span className="text-xs font-mono bg-white text-blue-600 px-2 py-1 rounded border border-blue-200">
                                   {dbType}
                               </span>
                           </div>
                           
                           {dbType === 'External Redis' && (
                               <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800 mb-4 leading-relaxed">
                                   <i className="fa-solid fa-triangle-exclamation mr-1 text-amber-600"></i>
                                   <strong>性能提示：</strong> 当前连接的是外部 Redis，在 Serverless 环境下可能较慢。
                                   <br/>建议在 Vercel Marketplace 添加 <strong>Upstash for Redis</strong> 以启用高性能 Vercel KV。
                               </div>
                           )}

                           <button 
                               className="w-full bg-gray-50 p-4 rounded-lg border border-gray-200 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors text-left" 
                               onClick={(e) => handleToggleLock(e)}
                           >
                               <div>
                                   <div className="font-bold text-gray-800 flex items-center gap-2">
                                       锁定布局 
                                       {isLocked && <span className="text-xs bg-red-100 text-red-500 px-2 py-0.5 rounded-full">已开启</span>}
                                   </div>
                                   <div className="text-xs text-gray-500 mt-1">
                                       {isLocked ? '48小时后自动解锁' : '防止误操作修改'}
                                   </div>
                               </div>
                               <div className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 ease-in-out ${isLocked ? 'bg-green-500' : 'bg-gray-300'}`}>
                                   <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isLocked ? 'translate-x-6' : 'translate-x-1'}`} />
                               </div>
                           </button>
                           
                           <hr className="my-2 border-gray-100" />
                           
                           <button onClick={() => setSecStep('change_pwd')} disabled={isLocked} className={`w-full py-2 border border-gray-200 rounded hover:bg-gray-50 text-sm text-gray-700 ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}>
                               修改普通后台密码
                           </button>
                           <button onClick={initTotpSetup} className="w-full py-2 border border-gray-200 rounded hover:bg-gray-50 text-sm text-gray-700">
                               {hasTotpSetup ? '重置超级管理员' : '设置超级管理员 (Google Auth)'}
                           </button>
                      </div>
                  )}

                  {secStep === 'confirm_lock' && (
                       <div className="p-6">
                           <h4 className="font-bold text-center text-red-600 mb-2">确认锁定</h4>
                           <p className="text-sm text-gray-600 mb-4">锁定后，只有输入超级管理员动态密码才能解锁，或者等待 48 小时自动解锁。</p>
                           <button onClick={executeLock} className="w-full bg-red-600 text-white py-2 rounded font-bold">立即锁定</button>
                           <button onClick={() => setSecStep('menu')} className="w-full mt-2 text-gray-500 text-sm">取消</button>
                       </div>
                  )}

                  {secStep === 'unlock' && (
                      <div className="p-6">
                          <h4 className="font-bold text-center mb-4">解除锁定 / 确认操作</h4>
                          <p className="text-sm text-gray-600 mb-4 text-center">请输入超级管理员动态密码</p>
                          <input 
                            type="text" 
                            className="w-full border p-2 rounded text-center text-lg tracking-widest mb-4 bg-white text-gray-900 font-mono"
                            placeholder="000 000"
                            maxLength={6}
                            autoFocus
                            value={totpInput}
                            onChange={(e) => setTotpInput(e.target.value)}
                          />
                          <button onClick={handleUnlockVerify} className="w-full bg-green-500 text-white py-2 rounded font-bold">验证</button>
                          <button onClick={() => setSecStep('menu')} className="w-full mt-2 text-sm text-gray-500">返回</button>
                      </div>
                  )}

                  {secStep === 'verify_old_totp' && (
                      <div className="p-6">
                          <h4 className="font-bold text-center mb-4">验证身份</h4>
                          <p className="text-sm text-gray-600 mb-4 text-center">您正在请求重置超级管理员。<br/>请输入当前绑定的验证码以继续。</p>
                          <input 
                            type="text" 
                            className="w-full border p-2 rounded text-center text-lg tracking-widest mb-4 bg-white text-gray-900 font-mono"
                            placeholder="000 000"
                            maxLength={6}
                            autoFocus
                            value={totpInput}
                            onChange={(e) => setTotpInput(e.target.value)}
                          />
                          <button onClick={verifyOldTotpAndProceed} className="w-full bg-blue-500 text-white py-2 rounded font-bold">验证并重置</button>
                          <button onClick={() => setSecStep('menu')} className="w-full mt-2 text-sm text-gray-500">取消</button>
                      </div>
                  )}

                  {secStep === 'setup_totp' && (
                      <div className="p-6">
                          <h4 className="font-bold text-center mb-2">Authenticator 设置</h4>
                          
                          <div className="bg-gray-100 p-4 rounded mb-4 text-center break-all border border-gray-200">
                               <div className="text-xs text-gray-500 mb-2">密钥 (Secret Key) - 请手动输入</div>
                               <div className="font-mono text-2xl font-bold text-gray-800 tracking-wider select-all bg-white p-2 rounded border border-gray-300">
                                   {newTotpSecret}
                               </div>
                          </div>
                          
                          <p className="text-xs text-center text-gray-500 mb-4">
                              请在 Google Authenticator 中选择“手动输入验证码”并填入上方密钥。
                          </p>

                          <input 
                            type="text" 
                            className="w-full border p-2 rounded text-center text-lg tracking-widest mb-4 bg-white text-gray-900"
                            placeholder="输入新验证码"
                            maxLength={6}
                            value={totpInput}
                            onChange={(e) => setTotpInput(e.target.value)}
                          />
                          <button onClick={verifyAndSaveTotp} className="w-full bg-primary text-white py-2 rounded">验证并启用</button>
                          <button onClick={() => setSecStep('menu')} className="w-full mt-2 text-sm text-gray-500">取消</button>
                      </div>
                  )}

                  {secStep === 'change_pwd' && (
                      <div className="p-6">
                          <h4 className="font-bold mb-2">设置普通后台密码</h4>
                          <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                              密码必须以 <span className="font-mono font-bold text-primary">admin</span> 开头（例如 admin996）。
                              前台查询框据此区分“查姓名”和“进后台”，只有 admin 开头的输入才会被当作密码校验。
                          </p>
                          <input 
                            type="text" 
                            className="w-full border p-2 rounded mb-4 bg-white text-gray-900"
                            placeholder="新密码（admin 开头，6-50 位）"
                            value={newAdminPwd}
                            onChange={(e) => setNewAdminPwd(e.target.value)}
                          />
                          <button onClick={handleSetAdminPwd} className="w-full bg-primary text-white py-2 rounded">保存</button>
                          <button onClick={() => setSecStep('menu')} className="w-full mt-2 text-sm text-gray-500">返回</button>
                      </div>
                  )}
              </div>
          </div>
      )}

      <Spotlight targetId={activeSpotlight} onClose={() => setActiveSpotlight(null)} title="位置在此" />
    </div>
  );
}
